/**
 * Subscription Picker
 * -------------------
 * Steuert die Auswahl "Einmalig kaufen" vs. "Abonnieren & Sparen" auf der
 * Produktseite und schreibt die gewaehlte Selling-Plan-ID in das Produktformular.
 *
 * Preise werden NICHT im JS nachgerechnet, sondern aus der von Liquid
 * eingebetteten Preis-Map gelesen (variant.selling_plan_allocations). Damit
 * stimmen gestaffelte Rabatte und Fixbetraege exakt mit dem Checkout ueberein.
 *
 * Der Zustand ueberlebt ein Section-Rerender (Variantenwechsel), weil das
 * Custom Element beim Wiedereinhaengen aus stateMap restauriert.
 */
(function () {
  /** formId -> { type: 'onetime'|'subscribe', planId: string|null } */
  const stateMap = new Map();

  function formatMoney(cents) {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
    return (cents / 100).toLocaleString('de-DE', { style: 'currency', currency: currency });
  }

  class SubscriptionPicker extends HTMLElement {
    connectedCallback() {
      this.form = this.closest('form') || document.getElementById(this.getAttribute('form'));
      if (!this.form) return;

      // Abo-Pflichtprodukt: es gibt keine Einmalkauf-Option, der Picker waehlt
      // nur noch das Intervall.
      this.required = this.hasAttribute('data-sp-required');

      this.prices = this.readPrices();
      this.typeInputs = this.querySelectorAll('[data-sp-type]');
      this.planInputs = this.querySelectorAll('[data-sp-plan]');
      this.freqPanel = this.querySelector('[data-sp-frequency]');
      this.priceNote = this.querySelector('[data-sp-price-note]');
      this.badge = this.querySelector('[data-sp-badge]');
      this.hiddenInput = this.querySelector('[data-sp-hidden-input]');

      this.abortController = new AbortController();
      const signal = this.abortController.signal;

      this.typeInputs.forEach((input) => {
        input.addEventListener('change', () => this.onTypeChange(), { signal });
      });

      this.planInputs.forEach((input) => {
        input.addEventListener('change', () => this.apply(), { signal });
      });

      // Variantenwechsel ohne Rerender: Preise und Verfuegbarkeit neu bewerten.
      this.form.addEventListener('variant:change', () => this.refresh(), { signal });

      this.restore();
      this.refresh();
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
    }

    readPrices() {
      try {
        return JSON.parse(this.querySelector('script[data-sp-prices]').textContent);
      } catch (e) {
        // Ohne Preis-Map werden alle Plaene gesperrt und es wandert keine
        // Selling-Plan-ID ins Formular — das sieht im Shop aus wie ein
        // stummer Warenkorb-Fehler. Deshalb hier laut sein.
        console.warn('[subscription-picker] Preis-Map konnte nicht gelesen werden:', e);
        return {};
      }
    }

    /** Aktuell im Formular ausgewaehlte Varianten-ID. */
    currentVariantId() {
      const input = this.form.querySelector('input[name="id"]');
      return input ? String(input.value) : null;
    }

    currentVariantPrices() {
      const id = this.currentVariantId();
      return id ? this.prices[id] : null;
    }

    selectedPlanInput() {
      return [...this.planInputs].find((input) => input.checked && !input.disabled);
    }

    isSubscribing() {
      if (this.required) return true;
      return [...this.typeInputs].some((input) => input.value === 'subscribe' && input.checked);
    }

    onTypeChange() {
      if (this.isSubscribing()) {
        this.freqPanel.hidden = false;
        this.apply();
      } else {
        this.freqPanel.hidden = true;
        this.clear();
      }
    }

    /**
     * Bewertet nach Variantenwechsel neu: welche Plaene gibt es fuer diese
     * Variante, wie hoch ist der maximale Rabatt, stimmt der Preishinweis noch.
     */
    refresh() {
      const variantPrices = this.currentVariantPrices();

      // Plaene, die es fuer diese Variante nicht gibt, sperren.
      let firstAvailable = null;
      this.planInputs.forEach((input) => {
        const available = Boolean(variantPrices && variantPrices.plans[input.value]);
        input.disabled = !available;
        const label = this.querySelector('[data-sp-plan-label="' + input.value + '"]');
        if (label) label.classList.toggle('is-unavailable', !available);
        if (available && !firstAvailable) firstAvailable = input;
      });

      // Gesperrter Plan war ausgewaehlt -> auf den ersten verfuegbaren umlegen.
      const selected = this.selectedPlanInput();
      if (!selected && firstAvailable) firstAvailable.checked = true;

      this.updateBadge(variantPrices);

      // Variante ohne Abo-Option: Auswahl auf "Einmalig" zurueckdrehen. Bei
      // Abo-Pflichtprodukten gibt es dieses Ausweichen nicht — dort bleibt der
      // Picker stehen, die Variante ist schlicht nicht kaufbar.
      if ((!variantPrices || !firstAvailable) && !this.required) {
        const onetime = [...this.typeInputs].find((input) => input.value === 'onetime');
        if (onetime) onetime.checked = true;
        this.freqPanel.hidden = true;
        this.clear();
        this.hidden = true;
        return;
      }

      this.hidden = false;
      if (this.isSubscribing()) this.apply();
    }

    updateBadge(variantPrices) {
      if (!this.badge) return;

      let maxDiscount = 0;
      if (variantPrices) {
        Object.keys(variantPrices.plans).forEach((planId) => {
          const plan = variantPrices.plans[planId];
          if (plan.price < variantPrices.base) {
            const saving = Math.round(((variantPrices.base - plan.price) / variantPrices.base) * 100);
            if (saving > maxDiscount) maxDiscount = saving;
          }
        });
      }

      if (maxDiscount > 0) {
        this.badge.textContent = 'bis ' + maxDiscount + '% Rabatt';
        this.badge.hidden = false;
      } else {
        this.badge.hidden = true;
      }
    }

    /** Abo aktivieren: Plan-ID ins Formular schreiben und Preishinweis zeigen. */
    apply() {
      const plan = this.selectedPlanInput();
      const variantPrices = this.currentVariantPrices();
      if (!plan || !this.hiddenInput) return;

      this.hiddenInput.value = plan.value;
      this.hiddenInput.disabled = false;
      stateMap.set(this.form.id, { type: 'subscribe', planId: plan.value });

      const planPrice = variantPrices && variantPrices.plans[plan.value];
      if (!planPrice || !this.priceNote) return;

      if (planPrice.price < variantPrices.base) {
        this.priceNote.textContent =
          'Abo-Preis: ' + formatMoney(planPrice.price) + ' statt ' + formatMoney(variantPrices.base);
      } else {
        this.priceNote.textContent = 'Abo-Preis: ' + formatMoney(planPrice.price);
      }
      this.priceNote.hidden = false;
    }

    /** Einmalkauf: Plan-ID entfernen, damit sie nicht mitgesendet wird. */
    clear() {
      // Bei Abo-Pflichtprodukten darf die Plan-ID nie entfernt werden.
      if (this.required) return;
      if (this.hiddenInput) {
        this.hiddenInput.value = '';
        this.hiddenInput.disabled = true;
      }
      if (this.priceNote) this.priceNote.hidden = true;
      stateMap.set(this.form.id, { type: 'onetime', planId: null });
    }

    /** Zustand nach einem Section-Rerender wiederherstellen. */
    restore() {
      const saved = stateMap.get(this.form.id);
      if (!saved || saved.type !== 'subscribe') return;
      if (this.required) {
        // Kein Toggle vorhanden — nur das gewaehlte Intervall zurueckholen.
        if (saved.planId) {
          const planRadio = this.querySelector('[data-sp-plan][value="' + saved.planId + '"]');
          if (planRadio) planRadio.checked = true;
        }
        return;
      }

      const subscribeRadio = this.querySelector('[data-sp-type][value="subscribe"]');
      if (subscribeRadio) subscribeRadio.checked = true;
      this.freqPanel.hidden = false;

      if (saved.planId) {
        const planRadio = this.querySelector('[data-sp-plan][value="' + saved.planId + '"]');
        if (planRadio) planRadio.checked = true;
      }
    }
  }

  if (!customElements.get('subscription-picker')) {
    customElements.define('subscription-picker', SubscriptionPicker);
  }
})();
